/**
 * ReactionPicker + ReactionBar — V1.8.5
 *
 * Reusable across TeamTalk and DaddyTalk bubbles.
 * - `ReactionBar`: chips row beneath the bubble. Tap a chip to toggle
 *   your own reaction. Highlights the chip when the current user has
 *   reacted.
 * - `ReactionPicker`: floating 6-emoji popover. Rendered by the bubble's
 *   long-press/double-tap handler.
 *
 * Both are 100% presentational. The bubble owns the toggle handler and
 * calls the appropriate API (`/team-chat/messages/{id}/react` or
 * `/daddytalk/messages/{id}/react`).
 */
import React from "react";

export const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

export function ReactionPicker({ open, onPick, onStar, isStarred, onDismiss, anchor = "bottom" }) {
    if (!open) return null;
    return (
        <>
            <div
                data-testid="reaction-picker-scrim"
                onClick={onDismiss}
                className="fixed inset-0 z-40"
            />
            <div
                data-testid="reaction-picker"
                className={`absolute z-50 ${anchor === "top" ? "-top-12" : "-bottom-12"} left-1/2 -translate-x-1/2
                    flex items-center gap-0.5 px-2 py-1.5 rounded-full bg-white
                    shadow-lg ring-1 ring-ink/10 animate-in fade-in zoom-in-95 duration-100`}
            >
                {QUICK_EMOJIS.map((e) => (
                    <button
                        key={e}
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); onPick(e); }}
                        data-testid={`reaction-pick-${e}`}
                        className="text-xl leading-none px-1.5 py-1 rounded-full hover:bg-ink/5 active:scale-90 transition-transform"
                    >
                        {e}
                    </button>
                ))}
                {onStar && (
                    <>
                        <span className="w-px h-5 bg-ink/15 mx-1" aria-hidden="true" />
                        <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); onStar(); }}
                            data-testid="reaction-pick-star"
                            className={`text-xl leading-none px-1.5 py-1 rounded-full hover:bg-amber-100 active:scale-90 transition-transform ${isStarred ? "text-amber-500" : "text-ink/50"}`}
                            aria-label={isStarred ? "Unstar message" : "Star message"}
                        >
                            {isStarred ? "★" : "☆"}
                        </button>
                    </>
                )}
            </div>
        </>
    );
}

export function ReactionBar({ reactions, myUserId, onToggle, testIdPrefix = "reaction" }) {
    if (!reactions || Object.keys(reactions).length === 0) return null;
    // Preserve entry order (which mirrors first-reactor order).
    const entries = Object.entries(reactions).filter(([, uids]) => (uids || []).length > 0);
    if (entries.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1 mt-1" data-testid={`${testIdPrefix}-bar`}>
            {entries.map(([emoji, uids]) => {
                const count = uids.length;
                const mine = myUserId && uids.includes(myUserId);
                return (
                    <button
                        key={emoji}
                        type="button"
                        onClick={() => onToggle(emoji)}
                        data-testid={`${testIdPrefix}-chip-${emoji}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ring-1 transition
                            ${mine
                                ? "bg-cobalt/10 ring-cobalt/40 text-cobalt"
                                : "bg-white ring-ink/10 text-ink/70 hover:ring-ink/20"}`}
                    >
                        <span className="text-sm leading-none">{emoji}</span>
                        {count > 1 && <span className="font-semibold tabular-nums">{count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * QuickReactionsHoverStrip — V1.9.29
 *
 * A small floating strip of the 6 quick emojis that appears on HOVER
 * over a bubble. Desktop / hover-capable devices only (mobile keeps
 * long-press). Position: just above the bubble edge, aligned to the
 * peer/self side. Parent MUST wrap in a `group relative` container.
 *
 * Rendering rule: use Tailwind's arbitrary media query
 * `[@media(hover:hover)]:group-hover:flex` so the strip only reveals on
 * true-hover devices — never flashes on mobile tap.
 */
export function QuickReactionsHoverStrip({ onPick, onStar, isStarred, isMine, testIdPrefix = "quick-react" }) {
    return (
        <div
            data-testid={`${testIdPrefix}-strip`}
            className={`absolute -top-8 z-20 hidden [@media(hover:hover)]:group-hover:flex
                items-center gap-0.5 px-1.5 py-1 rounded-full bg-white/95 backdrop-blur
                shadow-md ring-1 ring-ink/10 select-none
                ${isMine ? "right-0" : "left-0"}`}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            {QUICK_EMOJIS.map((e) => (
                <button
                    key={e}
                    type="button"
                    onClick={(ev) => { ev.stopPropagation(); onPick(e); }}
                    data-testid={`${testIdPrefix}-${e}`}
                    className="text-[15px] leading-none px-1 py-0.5 rounded-full hover:bg-ink/10 hover:scale-125 active:scale-95 transition-transform"
                    aria-label={`React ${e}`}
                >
                    {e}
                </button>
            ))}
            {onStar && (
                <>
                    <span className="w-px h-4 bg-ink/15 mx-1" aria-hidden="true" />
                    <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); onStar(); }}
                        data-testid={`${testIdPrefix}-star`}
                        className={`text-[15px] leading-none px-1 py-0.5 rounded-full hover:bg-amber-100 hover:scale-125 active:scale-95 transition-transform ${isStarred ? "text-amber-500" : "text-ink/40"}`}
                        aria-label={isStarred ? "Unstar message" : "Star message"}
                    >
                        {isStarred ? "★" : "☆"}
                    </button>
                </>
            )}
        </div>
    );
}

/**
 * useLongPress — call `handlers` from the bubble to open the picker on
 * long-press (mobile) or dblclick (desktop).
 *
 *   const openers = useLongPress(() => setPickerOpen(true));
 *   <div {...openers} onDoubleClick={() => setPickerOpen(true)} />
 */
export function useLongPress(onLongPress, { delayMs = 450 } = {}) {    const timerRef = React.useRef(null);
    const firedRef = React.useRef(false);
    const start = React.useCallback(() => {
        firedRef.current = false;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            firedRef.current = true;
            onLongPress();
        }, delayMs);
    }, [onLongPress, delayMs]);
    const cancel = React.useCallback(() => {
        clearTimeout(timerRef.current);
    }, []);
    return {
        onTouchStart: start,
        onTouchEnd: cancel,
        onTouchCancel: cancel,
        onTouchMove: cancel,
        onMouseDown: start,
        onMouseUp: cancel,
        onMouseLeave: cancel,
    };
}
